import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { dspApi } from "../../repositories/dsp";
import { DSPData } from "../../components/types/types";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";
import { formatDSPName } from "../../utils/formatDSP";
import { useCredits } from "../../hooks/useCredits";
import PageHeader from "../../components/ui/PageHeader";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import DataTable, { DataTableColumn, rowActionIconClass } from "../../components/ui/DataTable";
import StatCard from "../../components/ui/StatCard";
import Pagination from "../../components/common/Pagination";
import { CreditCard } from "lucide-react";

/**
 * A DSP account password. Held back behind a per-row reveal rather than printed
 * in the clear: the table is readable over a shoulder or in a screen share, and
 * nothing here needs every password visible at once. Revealing is one click, so
 * the admin loses no access.
 */
const PasswordCell: React.FC<{ value?: string | null }> = ({ value }) => {
  const [revealed, setRevealed] = useState(false);

  if (!value) return <span className="text-neutral-400">—</span>;

  return (
    <div className="flex items-center gap-1">
      <span className={revealed ? "font-mono" : "tracking-widest text-neutral-500"}>
        {revealed ? value : "••••••••"}
      </span>
      <button
        type="button"
        onClick={() => setRevealed((prev) => !prev)}
        className={rowActionIconClass}
        aria-label={revealed ? "Hide password" : "Show password"}
        title={revealed ? "Hide password" : "Show password"}
      >
        {revealed ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
      </button>
    </div>
  );
};

const UserDsps: React.FC = () => {
  const [dspList, setDspList] = useState<DSPData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const { id } = useParams<{ id: string }>();
  const {
    getAllUserCredits,
    credits,
    totalAvailableCredits,
    pagination,
    loading: creditsLoading,
  } = useCredits();


  const fetchDSPs = async () => {
    try {
      setLoading(true);
      if (!id) {
        throw new Error("ID is required to fetch DSPs");
      }
      const response = await dspApi.getDspById(id);
      setDspList(response.data.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setDspList([]);
      } else {
        Swal.fire(
          "Error",
          error?.response?.data?.message ||
            error.message ||
            "An unknown error occurred",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDSPs();
  }, [id]);

  useEffect(() => {
    if (id) {
      getAllUserCredits({ id: parseInt(id), page: currentPage, limit: itemsPerPage });
    }
  }, [id, currentPage]);

  const creditColumns: DataTableColumn<any>[] = [
    {
      key: "credits",
      header: "Credits",
      cell: (credit) => <span className="font-medium tabular-nums">{credit.credits}</span>,
    },
    {
      key: "creditType",
      header: "Type",
      cell: (credit) => (
        <span className="capitalize text-neutral-600">{credit.creditType || "—"}</span>
      ),
    },
    {
      key: "expiresAt",
      header: "Expires",
      cell: (credit) => (
        <span className="text-neutral-600">
          {credit.expiresAt ? new Date(credit.expiresAt).toLocaleDateString() : "—"}
        </span>
      ),
    },
  ];

  const dspColumns: DataTableColumn<DSPData>[] = [
    {
      key: "id",
      header: "ID",
      cell: (dsp) => <span className="tabular-nums text-neutral-600">{dsp.id}</span>,
    },
    {
      key: "name",
      header: "Name",
      cell: (dsp) => <span className="font-medium whitespace-nowrap">{dsp.name}</span>,
    },
    {
      key: "email",
      header: "Email",
      cell: (dsp) => <span className="text-neutral-600">{dsp.email || "—"}</span>,
    },
    {
      key: "password",
      header: "Password",
      cell: (dsp) => <PasswordCell value={dsp.password} />,
    },
    {
      key: "type",
      header: "Type",
      cell: (dsp) => <span className="text-neutral-600">{formatDSPName(dsp.type)}</span>,
    },
  ];

  return (
    <div className="py-2 sm:p-6">
      {/* Opened from a user row on the admin dashboard. */}
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "User DSP Accounts" }]}
        className="mb-6"
        title="User DSP Accounts"
        description="Credits and connected DSP accounts for this user."
      />

      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:max-w-2xl">
        <StatCard
          label="Total Available Credits"
          value={totalAvailableCredits}
          icon={<CreditCard className="w-5 h-5" />}
        />
      </div>

      <h2 className="mb-3 text-lg font-semibold text-black">Credit Details</h2>
      <DataTable
        columns={creditColumns}
        rows={credits ?? []}
        getRowKey={(_credit, index) => index}
        loading={creditsLoading}
        skeletonRows={itemsPerPage}
        emptyMessage={
          <span className="block">
            <span className="block text-sm font-medium text-black">No credit details available</span>
            <span className="block mt-1 text-sm text-neutral-500">
              This user has no credit records yet.
            </span>
          </span>
        }
      />

      {/* Shared Pagination, replacing a hand-rolled copy of the same control. */}
      {pagination.totalItems > 0 && (
        <div className="mt-4">
          <Pagination
            page={pagination.currentPage}
            totalPages={pagination.totalPages}
            setPage={setCurrentPage}
            totalItems={pagination.totalItems}
            limit={pagination.limit}
          />
        </div>
      )}

      <h2 className="mt-8 mb-3 text-lg font-semibold text-black">DSP Accounts</h2>
      <DataTable
        columns={dspColumns}
        rows={dspList}
        getRowKey={(dsp) => dsp.id}
        loading={loading}
        emptyMessage={
          <span className="block">
            <span className="block text-sm font-medium text-black">No DSP accounts found</span>
            <span className="block mt-1 text-sm text-neutral-500">
              This user has not connected any DSP accounts.
            </span>
          </span>
        }
      />
    </div>
  );
};

export default UserDsps;
